import { collectionsService } from '@services/firebase/firestore/functionFactory';
import { permissionsService } from '@services/permissions';
import { appUserStore } from '@stores/bootstrap-stores/user';
import { log } from '@utils/log';
import { distinctStrArr } from '@utils/vanillaJs';
import { PermissionId, permissions, Role } from '@vincerosoft/assessfluence-types';
import { QueryConstraint, Timestamp, where } from 'firebase/firestore';
import { accountStore } from 'src/stores/bootstrap-stores/account';

export const normalizeRolePermissions = (role: Role) => {
  const permissionByUserType = permissions.filter((perm) =>
    perm?.user_types?.includes(role.user_type)
  );

  let perms = role.permissions;

  if (role.permissions.length) {
    perms = role.permissions.map((perm) => {
      const isValidPerm = permissionByUserType.some((p) => p.id === perm);

      if (!isValidPerm) {
        log.warn(`Invalid permission "${perm}" for access level "${role.user_type}"`);
      }
      return perm;
    });
  }

  return distinctStrArr(perms) as PermissionId[];
};

function rolesFactory() {
  const getRoles = async () => {
    const account = accountStore.getState().account;
    const userAL = permissionsService.userAccessLevel();

    const entityTypes = ['admin', 'user', 'guest'];
    const query = [];

    const queryValue =
      userAL === 'tenant'
        ? ['tenant', 'owner', 'admin']
        : userAL === 'owner'
          ? ['owner', 'admin']
          : userAL === 'admin'
            ? // everything but owner
              entityTypes
            : userAL === 'user'
              ? // everything but owner and admin
                entityTypes.slice(1)
              : ['guest'];

    query.push(where('user_type', 'in', queryValue));

    if (userAL === 'tenant') {
      query.push(where('domain', '==', 'TENANT'));
    } else if (userAL == 'owner') {
      query.push(where('group_entity_id', '==', account.id));
    } else {
      query.push(where('entity_id', '==', account.id));
    }

    const roles = await collectionsService.roles.getDocs(query, {
      dataConverter: {
        fromFirestore: (snapshot) => {
          const data = snapshot.data() as Role;
          const normalized = normalizeRolePermissions(data);

          if (data.created_at && data.created_at instanceof Timestamp) {
            data.created_at = (data.created_at as Timestamp).toDate();
          }

          if (data.updated_at && data.updated_at instanceof Timestamp) {
            data.updated_at = (data.updated_at as Timestamp).toDate();
          }

          return {
            ...data,
            id: snapshot.id,
            permissions: normalized
          };
        },
        toFirestore: () => ({}) as Role
      }
    });

    const currentUserRole = appUserStore.getState().role;

    const signedInUserRole = roles.find((r) => r.id === currentUserRole?.id);

    const hasUserRole = roles.find((r) => signedInUserRole && r.id === signedInUserRole.id);

    if (!hasUserRole && signedInUserRole) {
      roles.push(signedInUserRole);
    }

    const uniqueRoles = roles.reduce((acc, curr) => {
      {
        if (!acc.find((r) => r.id === curr.id)) {
          acc.push(curr);
        }
        return acc;
      }
    }, [] as Role[]);

    return uniqueRoles;
  };

  const getOwnerRoles = async () => {
    const { scope, scopeQuery } = accountStore.getState();

    if (!['tenant', 'group'].includes(scope)) {
      return [];
    }

    const query: QueryConstraint[] = [where('user_type', '==', 'owner'), scopeQuery];

    return collectionsService.roles.getDocs(query, {});
  };

  const getAdminRoles = async () => {
    const { scopeQuery } = accountStore.getState();

    const query: QueryConstraint[] = [where('user_type', '==', 'admin'), scopeQuery];

    return collectionsService.roles.getDocs(query, {});
  };

  return {
    getRoles,
    getOwnerRoles,
    getAdminRoles
  };
}

export const rolesService = rolesFactory();
